import React from 'react'
import { Box, Text } from '@common/fundamentals'
import { Button, IconButton, LoadingButton } from '@common/material'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { localizedStrings } from '@core/strings'
import { useUpdateDocumentAppraisal } from '@core/react-query/features/siteBoxDocuments'
import { useToast } from '@core/toast'
import { ModalDialogContainer } from '@common/modal'
import {
  ISiteBoxDocumentConfigResult,
  SiteBoxDocumentConfigStatus,
} from '@core/entities/SiteBoxDocumentConfig'
import { SiteBoxDocumentConfigResult } from '@core/entities/SiteBoxDocumentConfig'
import { DocumentIntelligence } from '@core/entities/SiteBoxDocument'
import { AppraisalStatusButtons } from '../components'
import { DatePicker } from '@common/material/DatePicker'
import fileDownload from 'js-file-download'
import { useSiteBoxAppraisalDownload } from '@core/utils/useSiteBoxAppraisalDownload'
import { mergePdfsWithPspdfkit } from 'webapp/utils/mergePdfsWithPspdfkit'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'

type DocumentAcceptanceStatus = Extract<
  SiteBoxDocumentConfigStatus,
  'PASS' | 'FAIL'
>

type DocumentAcceptanceFormValues = {
  documentAcceptance: DocumentAcceptanceStatus | null
  downloadReceipt: boolean
  expiryDate: string | null
}

export type DocumentAcceptanceModalProps = {
  documentId: number
  results: ISiteBoxDocumentConfigResult[]
  documentName?: string
  documentDownloadUrl?: string
  allowExpiry?: boolean
  onSuccess: () => void
  onClose: () => void
}

export const DocumentAcceptanceModal: React.FC<
  DocumentAcceptanceModalProps
> = ({
  documentId,
  results,
  documentName,
  documentDownloadUrl,
  allowExpiry = false,
  onSuccess,
  onClose,
}) => {
  const updateDocumentAppraisal = useUpdateDocumentAppraisal()
  const toast = useToast()
  const { fetchAppraisalPdfBytes, fetchPdfBytes, mergePdfsToBytes } =
    useSiteBoxAppraisalDownload({ mergeFn: mergePdfsWithPspdfkit })

  const { control, handleSubmit, watch, setValue, clearErrors } =
    useForm<DocumentAcceptanceFormValues>({
      defaultValues: {
        documentAcceptance: null,
        downloadReceipt: false,
        expiryDate: null,
      },
    })

  const documentAcceptance = watch('documentAcceptance')
  const isExpiryRequired = allowExpiry && documentAcceptance === 'PASS'

  const total = results.length
  const reviewed = results.filter((r) =>
    SiteBoxDocumentConfigResult.new(r).isAppraised(),
  ).length
  const passed = results.filter((r) => r.userResponse === 'PASS').length

  const runDownloadWithAppraisal = async () => {
    if (!documentName || !documentDownloadUrl) return
    const toastId = uuidv4()
    toast.progress(localizedStrings.downloadingDocument, {
      duration: 120 * 1000,
      id: toastId,
    })
    try {
      const [appraisalPdfBytes, siteBoxPdfBytes] = await Promise.all([
        fetchAppraisalPdfBytes(documentId),
        fetchPdfBytes(documentDownloadUrl),
      ])
      if (!appraisalPdfBytes) {
        toast.error(localizedStrings.downloadFailed, { id: toastId })
        return
      }
      const mergedPdfBytes = await mergePdfsToBytes(
        appraisalPdfBytes,
        siteBoxPdfBytes,
      )
      const blob = new Blob([new Uint8Array(mergedPdfBytes)], {
        type: 'application/pdf',
      })
      const filename = documentName || `document-${documentId}`
      fileDownload(blob, filename)
      toast.success(localizedStrings.downloadComplete, { id: toastId })
    } catch {
      toast.error(localizedStrings.downloadFailed, { id: toastId })
    }
  }

  const onSubmit = ({
    documentAcceptance,
    downloadReceipt,
    expiryDate,
  }: DocumentAcceptanceFormValues) => {
    if (!documentAcceptance) return
    const shouldDownloadReceipt =
      downloadReceipt && documentName && documentDownloadUrl
    updateDocumentAppraisal.mutate(
      {
        documentId,
        status: documentAcceptance,
        ...(isExpiryRequired && expiryDate ? { expiryDate } : {}),
      },
      {
        onSuccess: () => {
          toast.success(localizedStrings.appraisalSubmitted)
          onSuccess()
          if (shouldDownloadReceipt) {
            runDownloadWithAppraisal()
          }
        },
        onError: (error) => {
          toast.error(error.message)
        },
      },
    )
  }

  return (
    <ModalDialogContainer onClose={onClose} maxWidth='xs'>
      <DialogTitle
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1 }}
      >
        {localizedStrings.documentSignOffPassFail}
        <IconButton onClick={onClose}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Text fontSize={12} fontWeight={700} mb={2}>
            {localizedStrings.documentQuestionsReviewedWithCompliance(
              reviewed,
              total,
              passed,
            )}
          </Text>
          <Text fontSize={12} mb={2}>
            {localizedStrings.selectBelowToAssignStatus}
          </Text>

          <Box mb={1}>
            <Controller
              name='documentAcceptance'
              control={control}
              render={({ field }) => (
                <AppraisalStatusButtons
                  value={field.value}
                  onChange={(status) => {
                    field.onChange(status)
                    clearErrors('expiryDate')
                    if (status !== 'PASS') {
                      setValue('expiryDate', null)
                    }
                  }}
                  variant='document'
                  size='small'
                />
              )}
            />
          </Box>

          {isExpiryRequired && (
            <Box mb={1} mt={2}>
              <Controller
                name='expiryDate'
                control={control}
                rules={{
                  required: localizedStrings.expiryDateRequired,
                  validate: (date) =>
                    DocumentIntelligence.isExpiryDateInFuture(date) ||
                    localizedStrings.expiryDateMustBeInFuture,
                }}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label={localizedStrings.expiryDate}
                    minDate={moment().add(1, 'day')}
                    maxDate={moment().add(3, 'months')}
                    value={field.value ? moment(field.value) : null}
                    onChange={(v) =>
                      field.onChange(
                        v ? moment(v).endOf('day').toISOString() : null,
                      )
                    }
                    slotProps={{
                      textField: {
                        required: true,
                        fullWidth: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          <Controller
            name='downloadReceipt'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label={
                  <Text variant='body2' fontSize={12}>
                    {localizedStrings.downloadAppraisalReceiptOnSubmission}
                  </Text>
                }
              />
            )}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type='button' variant='outlined' onClick={onClose}>
            {localizedStrings.cancel}
          </Button>
          <LoadingButton
            type='submit'
            variant='contained'
            loading={updateDocumentAppraisal.isLoading}
            disabled={!documentAcceptance}
          >
            {localizedStrings.submit}
          </LoadingButton>
        </DialogActions>
      </form>
    </ModalDialogContainer>
  )
}
